import { NavigateOptions, useLocation, useNavigate, useParams } from 'react-router-dom';

import { selectLiffId, selectLogSessionToken } from '../../redux/auth';

import { entryAPI } from '../../../api/log';

import { getNavigateLogEntry } from '../../helper';
import useCallback from '../useCallback';
import useSelector from '../useSelector';

export interface ErrorPageLocationState {
  errorMessage: string;
}

export interface NavigateOptionsForErrorPage extends NavigateOptions {
  state: ErrorPageLocationState;
}

const useCustomNavigate = () => {
  const _id = useSelector(selectLiffId);
  const logSessionToken = useSelector(selectLogSessionToken);

  const nativeNavigate = useNavigate();

  const location = useLocation();
  const sourceRoute = location.pathname;

  const navigate = (
    targetRoute: string,
    options?: NavigateOptionsForErrorPage | NavigateOptions,
  ) => {
    const { level, msg, payload } = getNavigateLogEntry(sourceRoute, targetRoute);

    if (logSessionToken && _id) {
      entryAPI.Post({
        _id,
        sessionToken: logSessionToken,
        log: {
          level,
          msg,
          payload,
        },
      });
    }

    nativeNavigate(targetRoute, options);
  };

  return { navigate };
};

export type NavigateErrorFn = (errorCode: string, errorRoute: string) => void;

export type NavigateFn = (
  targetRoute: string,
  options?: NavigateOptions | NavigateOptionsForErrorPage,
) => void;

export function useRouter() {
  const { navigate } = useCustomNavigate();

  const navigateError: NavigateErrorFn = useCallback((errorCode, errorRoute) => {
    const redirectRoute = `${errorRoute}/${errorCode}`;
    navigate(redirectRoute);
  }, []);

  return {
    navigate,
    navigateError,
    useParams,
    useLocation,
  };
}
